/**
 * Client Word utilisant @devana/ws-tools
 */

import { DevanaWSClient } from "@devana/ws-tools";

// Créer le client Devana
export function createWordClient(
  apiUrl: string,
  options?: {
    documentName?: string;
    bearerToken?: string;
    callbacks?: {
      onConnected?: (sessionId: string) => void;
      onReconnected?: (sessionId: string) => void;
      onDisconnected?: () => void;
      onError?: (error: Error) => void;
      onSessionsChanged?: () => void;
    };
  }
) {
  // Essayer de récupérer le nom du document depuis Word
  let docName = options?.documentName;

  if (!docName && typeof Office !== "undefined" && Office.context?.document) {
    // Tenter de récupérer le nom du fichier Word
    try {
      docName =
        (Office.context.document as any).url
          ?.split("/")
          .pop()
          ?.replace(".docx", "") || "Document Word";
    } catch (e) {
      docName = "Document Word";
    }
  }

  const client = new DevanaWSClient({
    apiUrl,
    clientType: "word",
    documentName: docName,
    bearerToken: options?.bearerToken, // Passer le bearer token (le serveur extrait le userId)
    autoReconnect: true, // Activer comme Chrome pour gérer les déconnexions
    maxReconnectAttempts: 5,
    reconnectDelay: 2000,
    metadata: {
      host: (Office?.context?.host as any) || "unknown",
      platform: (Office?.context?.platform as any) || "unknown",
    },
    onConnected: (sessionId) => {
      console.log("[WordClient] ✅ Connected with session:", sessionId);
      console.log("[WordClient] Document:", docName);

      // Ré-enregistrer les tools à chaque connexion (comme Chrome)
      console.log("[WordClient] 📝 Re-registering tools after connection...");
      registerWordTools(client);

      options?.callbacks?.onConnected?.(sessionId);
    },
    onReconnected: (sessionId) => {
      console.log("[WordClient] 🔄 Reconnected with session:", sessionId);

      // Ré-enregistrer les tools après reconnexion (comme Chrome)
      console.log("[WordClient] 📝 Re-registering tools after reconnection...");
      registerWordTools(client);

      options?.callbacks?.onReconnected?.(sessionId);
    },
    onDisconnected: () => {
      console.log("[WordClient] ⚠️ Disconnected");
      options?.callbacks?.onDisconnected?.();
    },
    onError: (error) => {
      console.error("[WordClient] ❌ Error:", error);
      options?.callbacks?.onError?.(error);
    },
    onSessionsChanged: () => {
      console.log("[WordClient] 🔄 Sessions changed notification");
      options?.callbacks?.onSessionsChanged?.();
    },
  });

  // Enregistrer tous les tools Word (première fois)
  registerWordTools(client);

  // Les tools cross-session (list_sessions, execute_on_session) sont ajoutés
  // automatiquement côté serveur via getToolsFromRagMode, pas ici

  return client;
}

/**
 * Enregistre tous les tools Word avec leurs handlers
 */
function registerWordTools(client: DevanaWSClient) {
  // === BASIC TOOLS ===

  client.registerTool("get_document_info", {
    description: "Get document structure including page count",
    schema: {
      type: "object",
      properties: {},
      required: [],
    },
    handler: async () => {
      return Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;

        body.load("text");
        paragraphs.load("items");
        await context.sync();

        const totalParagraphs = paragraphs.items.length;
        const totalWords = body.text
          .split(/\s+/)
          .filter((w) => w.length > 0).length;

        // Estimation du nombre de pages (l'API Word ne donne pas directement accès au vrai nombre)
        let totalPages = 0;

        try {
          // Méthode 1: Chercher un champ NUMPAGES (rare mais possible)
          const fields = body.fields;
          fields.load("items");
          await context.sync();

          for (let field of fields.items) {
            try {
              field.load("code");
              await context.sync();

              // Vérifier si c'est un champ NUMPAGES
              if (field.code && field.code.toUpperCase().includes("NUMPAGES")) {
                // Essayer de récupérer le résultat
                field.load("result");
                await context.sync();

                if (field.result) {
                  // Le result peut être un Range object
                  const resultRange = field.result as Word.Range;
                  resultRange.load("text");
                  await context.sync();

                  const resultText = resultRange.text;
                  const match = resultText.match(/\d+/);
                  if (match) {
                    totalPages = parseInt(match[0]);
                    console.log(`[get_document_info] Found NUMPAGES field: ${totalPages} pages`);
                    break;
                  }
                }
              }
            } catch (fieldError) {
              // Ignorer les erreurs sur les champs individuels
              console.log("[get_document_info] Error reading field:", fieldError);
            }
          }
        } catch (e) {
          console.log("[get_document_info] No fields or error reading fields:", e);
        }

        // Si on n'a pas trouvé de champ NUMPAGES, estimer
        if (totalPages === 0) {
          // Estimation plus précise basée sur plusieurs facteurs
          // Une page A4 typique contient :
          // - ~500-600 mots avec police 11-12pt, interligne normal
          // - ~25-30 paragraphes normaux
          // - ~40-50 lignes de texte

          // Calculer selon différents critères et prendre la moyenne
          const estimateByWords = Math.ceil(totalWords / 500); // ~500 mots par page
          const estimateByParagraphs = Math.ceil(totalParagraphs / 25); // ~25 paragraphes par page
          const estimateByCharacters = Math.ceil(body.text.length / 3000); // ~3000 caractères par page

          // Prendre le maximum des estimations (plus conservateur)
          totalPages = Math.max(estimateByWords, estimateByParagraphs, estimateByCharacters);

          // S'assurer qu'on a au moins 1 page
          totalPages = Math.max(1, totalPages);

          console.log(`[get_document_info] Page estimation: words=${estimateByWords}, paragraphs=${estimateByParagraphs}, chars=${estimateByCharacters} → ${totalPages} pages`);
        }

        // Aperçu des premiers paragraphes (AVEC les vides pour garder les bons numéros!)
        const previewCount = Math.min(20, totalParagraphs); // Augmenté à 20 pour plus de contexte
        const paragraphPreviews: Array<{
          number: number;
          preview: string;
          isEmpty: boolean;
          style?: string;
        }> = [];

        for (let i = 0; i < previewCount; i++) {
          const para = paragraphs.items[i];
          para.load(["text", "style", "styleBuiltIn"]);
        }
        await context.sync();

        for (let i = 0; i < previewCount; i++) {
          const para = paragraphs.items[i];
          const text = para.text.trim();
          const style = para.style || para.styleBuiltIn || "Normal";

          // TOUJOURS inclure le paragraphe, même vide, pour garder les numéros corrects
          paragraphPreviews.push({
            number: i + 1,
            preview: text.length > 0
              ? (text.length > 100 ? text.substring(0, 100) + "..." : text)
              : "[Paragraphe vide]",
            isEmpty: text.length === 0,
            style: style
          });
        }

        // Compter aussi les tables
        const tables = context.document.body.tables;
        tables.load("items");
        await context.sync();
        const totalTables = tables.items.length;

        // Compter les lignes - chaque paragraphe est au minimum une ligne
        // Plus les retours à la ligne dans le texte
        let totalLines = totalParagraphs; // Au minimum, chaque paragraphe = 1 ligne

        // Ajouter les lignes supplémentaires dans les paragraphes longs
        for (let i = 0; i < paragraphs.items.length; i++) {
          const paraText = paragraphs.items[i].text;
          // Estimer ~80 caractères par ligne en moyenne
          const estimatedLinesInPara = Math.max(1, Math.ceil(paraText.length / 80));
          totalLines += (estimatedLinesInPara - 1); // -1 car on a déjà compté 1 ligne par paragraphe
        }

        return {
          totalPages,
          totalParagraphs,
          totalWords,
          totalLines,
          totalTables,
          totalCharacters: body.text.length,
          paragraphPreviews,
          message: `Document contains ${totalPages} page(s), ${totalParagraphs} paragraphs, ${totalWords} words, ${totalLines} lines, ${totalTables} table(s).`,
        };
      });
    },
  });

  client.registerTool("get_content", {
    description: "Get document content with formatting and styles. For large documents, use startPage/endPage to get specific pages",
    schema: {
      type: "object",
      properties: {
        startPage: {
          type: "integer",
          description: "Start page number (1-based). If omitted, starts from beginning"
        },
        endPage: {
          type: "integer",
          description: "End page number (1-based). If omitted, goes to end"
        },
        maxParagraphs: {
          type: "integer",
          description: "Maximum number of paragraphs to return (default: 100 for safety)"
        }
      },
      required: [],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;

        // Charger les propriétés du document
        body.load("text");
        paragraphs.load("items");

        // Charger les sections pour compter les pages
        const sections = context.document.sections;
        sections.load("items");

        await context.sync();

        // Essayer de récupérer le nombre de pages
        let totalPages = 0;
        try {
          // Méthode 1: Via les propriétés du document
          const properties = context.document.properties;
          properties.load("customProperties");
          await context.sync();

          // Méthode 2: Estimation basée sur les sections
          // Dans Word, chaque section peut avoir plusieurs pages
          // On va utiliser une approche différente avec les paragraphes

          // Pour obtenir le vrai nombre de pages, on doit utiliser les champs
          const fields = body.fields;
          fields.load("items");
          await context.sync();

          // Chercher un champ NUMPAGES
          for (let field of fields.items) {
            field.load("code, result");
            await context.sync();
            if (field.code && field.code.includes("NUMPAGES")) {
              // field.result peut être un Range, donc on charge son texte
              if (field.result) {
                (field.result as any).load("text");
                await context.sync();
                const resultText = (field.result as any).text || String(field.result);
                const match = String(resultText).match(/\d+/);
                if (match) {
                  totalPages = parseInt(match[0]);
                  break;
                }
              }
            }
          }

          // Si on n'a pas trouvé, on estime
          if (totalPages === 0) {
            // Estimation basique: ~500 mots par page
            const wordCount = body.text.split(/\s+/).filter(w => w.length > 0).length;
            totalPages = Math.max(1, Math.ceil(wordCount / 500));
          }
        } catch (e) {
          // Estimation basique si erreur
          const wordCount = body.text.split(/\s+/).filter(w => w.length > 0).length;
          totalPages = Math.max(1, Math.ceil(wordCount / 500));
        }

        // Limites de sécurité pour éviter les surcharges
        const maxParagraphsLimit = data?.maxParagraphs || 100;
        const startPage = data?.startPage || 1;
        const endPage = data?.endPage || totalPages;

        // Validation des paramètres
        if (startPage < 1 || startPage > totalPages) {
          throw new Error(`startPage ${startPage} invalide. Le document a ${totalPages} pages.`);
        }
        if (endPage < startPage || endPage > totalPages) {
          throw new Error(`endPage ${endPage} invalide. Doit être entre ${startPage} et ${totalPages}.`);
        }

        // Calculer approximativement quels paragraphes correspondent aux pages demandées
        // Estimation: répartir les paragraphes uniformément sur les pages
        const paragraphsPerPage = Math.ceil(paragraphs.items.length / totalPages);
        const startParagraphIndex = Math.max(0, (startPage - 1) * paragraphsPerPage);
        const endParagraphIndex = Math.min(paragraphs.items.length, endPage * paragraphsPerPage);

        // Limiter davantage si nécessaire
        const actualEndIndex = Math.min(endParagraphIndex, startParagraphIndex + maxParagraphsLimit);

        // Message d'avertissement si on a dû tronquer
        let truncated = false;
        if (actualEndIndex < endParagraphIndex) {
          truncated = true;
        }

        // Récupérer le contenu avec styles pour chaque paragraphe dans la plage
        const contentWithStyles = [];

        for (let i = startParagraphIndex; i < actualEndIndex; i++) {
          const para = paragraphs.items[i];

          // Charger toutes les propriétés de style
          para.load([
            "text",
            "style",
            "styleBuiltIn",
            "font",
            "alignment",
            "firstLineIndent",
            "leftIndent",
            "rightIndent",
            "spaceAfter",
            "spaceBefore",
            "lineSpacing",
            "lineUnitAfter",
            "lineUnitBefore",
            "outlineLevel"
          ]);

          // Charger aussi la police
          para.font.load([
            "name",
            "size",
            "bold",
            "italic",
            "underline",
            "color",
            "highlightColor"
          ]);
        }

        await context.sync();

        // Construire la structure de contenu enrichie
        for (let i = startParagraphIndex; i < actualEndIndex; i++) {
          const para = paragraphs.items[i];
          const text = para.text.trim();

          // Ignorer les paragraphes vides sauf s'ils ont un style spécial
          if (text.length === 0 && para.style === "Normal") {
            continue;
          }

          const paragraphInfo = {
            number: i + 1,
            text: text,
            style: para.style || para.styleBuiltIn || "Normal",
            formatting: {
              font: {
                name: para.font.name,
                size: para.font.size,
                bold: para.font.bold,
                italic: para.font.italic,
                underline: para.font.underline !== "None",
                color: para.font.color,
                highlight: para.font.highlightColor
              },
              paragraph: {
                alignment: para.alignment,
                firstLineIndent: para.firstLineIndent,
                leftIndent: para.leftIndent,
                rightIndent: para.rightIndent,
                spaceAfter: para.spaceAfter,
                spaceBefore: para.spaceBefore,
                lineSpacing: para.lineSpacing,
                outlineLevel: para.outlineLevel
              }
            },
            isEmpty: text.length === 0
          };

          contentWithStyles.push(paragraphInfo);
        }

        // Compter aussi les tableaux pour info complète
        const tables = body.tables;
        tables.load("items");
        await context.sync();

        // Construire le texte brut seulement pour les paragraphes récupérés
        let partialText = "";
        for (let i = startParagraphIndex; i < actualEndIndex; i++) {
          partialText += paragraphs.items[i].text + "\n";
        }

        // Créer le message approprié
        let message = "";
        if (startPage === 1 && endPage === totalPages && !truncated) {
          message = `Document complet: ${totalPages} page(s), ${paragraphs.items.length} paragraphs, ${tables.items.length} table(s).`;
        } else {
          message = `Pages ${startPage} à ${endPage} sur ${totalPages}. `;
          message += `Paragraphes ${startParagraphIndex + 1} à ${actualEndIndex} sur ${paragraphs.items.length}. `;
          if (truncated) {
            message += `⚠️ Limité à ${maxParagraphsLimit} paragraphes pour éviter la surcharge.`;
          }
        }

        return {
          totalPages: totalPages,
          totalParagraphs: paragraphs.items.length,
          currentPage: startPage,
          endPage: endPage === totalPages ? totalPages : endPage,
          retrievedParagraphs: contentWithStyles.length,
          totalWords: body.text.split(/\s+/).filter(w => w.length > 0).length,
          totalCharacters: body.text.length,
          totalTables: tables.items.length,
          content: contentWithStyles,
          plainText: partialText,
          truncated: truncated,
          message: message
        };
      });
    },
  });

  client.registerTool("get_selection", {
    description: "Get selected text",
    schema: {
      type: "object",
      properties: {},
      required: [],
    },
    handler: async () => {
      return Word.run(async (context) => {
        const selection = context.document.getSelection();
        selection.load("text");
        await context.sync();
        return selection.text;
      });
    },
  });

  // === REDACTION TOOLS ===

  client.registerTool("add_paragraph", {
    description:
      "Add ONE paragraph with style. Call multiple times to build document.",
    schema: {
      type: "object",
      properties: {
        text: { type: "string", description: "Paragraph text (single line)" },
        style: {
          type: "string",
          enum: [
            "Title",
            "Heading1",
            "Heading2",
            "Heading3",
            "Normal",
            "Quote",
          ],
          description: "Paragraph style",
        },
        afterParagraphNumber: {
          type: "integer",
          description: "Insert after this paragraph",
        },
      },
      required: ["text", "style"],
    },
    handler: async (data) => {
      console.log("[add_paragraph] Starting handler");
      console.log("[add_paragraph] Text:", data.text.substring(0, 50));
      console.log("[add_paragraph] Style:", data.style);
      console.log(
        "[add_paragraph] After paragraph:",
        data.afterParagraphNumber
      );

      // Si le texte contient des \n, splitter automatiquement
      if (data.text.includes("\n")) {
        console.log(
          "[add_paragraph] ⚠️ Multi-line text detected, splitting..."
        );
        const lines = data.text
          .split("\n")
          .filter((line: string) => line.trim().length > 0);
        console.log("[add_paragraph] Split into", lines.length, "paragraphs");

        // Obtenir le nombre de paragraphes avant
        const infoBefore: any = await Word.run(async (ctx) => {
          const paras = ctx.document.body.paragraphs;
          paras.load("items");
          await ctx.sync();
          return { count: paras.items.length };
        });

        const startPosition = data.afterParagraphNumber || infoBefore.count;

        for (let i = 0; i < lines.length; i++) {
          const line = lines[i].trim();
          if (line.length === 0) continue;

          console.log(
            `[add_paragraph] Adding line ${i + 1}/${
              lines.length
            }: ${line.substring(0, 30)}...`
          );

          await addSingleParagraph(line, data.style, startPosition + i);
        }

        // Obtenir le nombre après pour confirmer
        const infoAfter: any = await Word.run(async (ctx) => {
          const paras = ctx.document.body.paragraphs;
          paras.load("items");
          await ctx.sync();
          return { count: paras.items.length };
        });

        const addedCount = infoAfter.count - infoBefore.count;
        const paragraphNumbers = Array.from(
          { length: addedCount },
          (_, i) => startPosition + i + 1
        );

        console.log(
          "[add_paragraph] ✅ All",
          addedCount,
          "paragraphs added at positions:",
          paragraphNumbers
        );

        return {
          count: addedCount,
          paragraphNumbers,
          startPosition: startPosition + 1,
          endPosition: startPosition + addedCount,
          message: `Added ${addedCount} paragraphs (positions ${
            startPosition + 1
          } to ${startPosition + addedCount}) with style ${data.style}`,
        };
      }

      // Cas normal: une seule ligne
      await addSingleParagraph(
        data.text,
        data.style,
        data.afterParagraphNumber
      );

      // Retourner aussi la position pour une seule ligne
      const info: any = await Word.run(async (ctx) => {
        const paras = ctx.document.body.paragraphs;
        paras.load("items");
        await ctx.sync();
        return { count: paras.items.length };
      });

      const position = data.afterParagraphNumber
        ? data.afterParagraphNumber + 1
        : info.count;

      return {
        count: 1,
        paragraphNumbers: [position],
        position,
        message: `Paragraph added at position ${position} with style ${
          data.style
        }: "${data.text.substring(0, 30)}..."`,
      };
    },
  });

  // Fonction helper pour ajouter un seul paragraphe
  async function addSingleParagraph(
    text: string,
    style: string,
    afterParagraphNumber?: number
  ): Promise<string> {
    // Vérifier qu'Office est disponible
    if (typeof Office === "undefined" || !Office.context) {
      throw new Error("Office context not available");
    }

    try {
      return await Word.run(async (context) => {
        console.log("[add_paragraph] Inside Word.run");

        // S'assurer qu'on travaille sur le bon document
        const doc = context.document;
        const body = doc.body;
        const paragraphs = body.paragraphs;

        // Charger aussi les propriétés du body pour debug
        body.load("text");
        paragraphs.load("items");

        console.log("[add_paragraph] Loading document...");
        await context.sync();

        console.log("[add_paragraph] Document loaded");
        console.log("[add_paragraph] Body text length:", body.text.length);
        console.log(
          "[add_paragraph] Paragraphs count:",
          paragraphs.items.length
        );

        // Stratégie différente: utiliser la sélection pour forcer Word à accepter
        let targetPara: Word.Paragraph;

        if (afterParagraphNumber && afterParagraphNumber > 0) {
          if (afterParagraphNumber > paragraphs.items.length) {
            throw new Error(
              `Paragraph ${afterParagraphNumber} does not exist (total: ${paragraphs.items.length})`
            );
          }
          targetPara = paragraphs.items[afterParagraphNumber - 1];
          console.log(
            "[add_paragraph] Target paragraph:",
            afterParagraphNumber
          );
        } else {
          targetPara = paragraphs.items[paragraphs.items.length - 1];
          console.log("[add_paragraph] Target: last paragraph");
        }

        // Sélectionner le paragraphe cible pour forcer le focus
        targetPara.select(Word.SelectionMode.end);
        await context.sync();

        console.log("[add_paragraph] Paragraph selected, now inserting...");

        // Insérer après la sélection
        const selection = doc.getSelection();
        const newPara = selection.insertParagraph(
          text,
          Word.InsertLocation.after
        );

        console.log("[add_paragraph] Paragraph created, applying style...");

        // Mapper les styles
        const styleMap: Record<string, Word.BuiltInStyleName> = {
          Title: Word.BuiltInStyleName.title,
          Heading1: Word.BuiltInStyleName.heading1,
          Heading2: Word.BuiltInStyleName.heading2,
          Heading3: Word.BuiltInStyleName.heading3,
          Normal: Word.BuiltInStyleName.normal,
          Quote: Word.BuiltInStyleName.quote,
        };

        const styleEnum = styleMap[style] || Word.BuiltInStyleName.normal;
        console.log("[add_paragraph] Setting style:", styleEnum);
        newPara.styleBuiltIn = styleEnum;

        console.log("[add_paragraph] Final sync...");
        await context.sync();
        console.log("[add_paragraph] ✅ Paragraph added to document!");

        return `Paragraph added: "${text.substring(
          0,
          30
        )}..." with style ${style}`;
      });
    } catch (error) {
      console.error("[add_paragraph] ❌ Error:", error);
      throw error;
    }
  }

  client.registerTool("add_list_item", {
    description: "Add ONE list item",
    schema: {
      type: "object",
      properties: {
        text: { type: "string" },
        listType: { type: "string", enum: ["bullet", "number"] },
        level: { type: "integer" },
        afterParagraphNumber: { type: "integer" },
      },
      required: ["text", "listType"],
    },
    handler: async (data) => {
      console.log("[add_list_item] Text:", data.text);
      console.log("[add_list_item] Type:", data.listType, "Level:", data.level);

      return Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        console.log(
          "[add_list_item] Document has",
          paragraphs.items.length,
          "paragraphs"
        );

        // Utiliser la même technique que add_paragraph
        let targetPara: Word.Paragraph;

        if (data.afterParagraphNumber && data.afterParagraphNumber > 0) {
          if (data.afterParagraphNumber > paragraphs.items.length) {
            throw new Error(
              `Paragraph ${data.afterParagraphNumber} does not exist`
            );
          }
          targetPara = paragraphs.items[data.afterParagraphNumber - 1];
        } else {
          targetPara = paragraphs.items[paragraphs.items.length - 1];
        }

        // Sélectionner et insérer
        targetPara.select(Word.SelectionMode.end);
        await context.sync();

        const selection = context.document.getSelection();
        const newPara = selection.insertParagraph(
          data.text,
          Word.InsertLocation.after
        );

        console.log("[add_list_item] Paragraph created, making it a list...");

        // Créer une liste
        newPara.startNewList();

        if (newPara.listItem) {
          newPara.listItem.level = data.level || 0;
        }

        await context.sync();

        // Obtenir la position du nouvel élément
        const allParas = context.document.body.paragraphs;
        allParas.load("items");
        await context.sync();

        console.log("[add_list_item] ✅ List item added");

        return {
          text: data.text,
          position: allParas.items.length,
          totalParagraphs: allParas.items.length,
          message: `List item added at position ${allParas.items.length}: "${data.text}"`,
        };
      });
    },
  });

  // === EDIT TOOLS ===

  client.registerTool("edit_content", {
    description: "Edit, insert, or delete content",
    schema: {
      type: "object",
      properties: {
        operation: {
          type: "string",
          enum: [
            "insert",
            "replace",
            "replaceParagraph",
            "delete",
            "deleteParagraph",
          ],
        },
        content: { type: "string" },
        paragraphNumber: { type: "integer" },
      },
      required: ["operation"],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        const totalParagraphs = paragraphs.items.length;

        switch (data.operation) {
          case "insert":
            if (!data.content)
              throw new Error("Content required for insert operation");
            const selection = context.document.getSelection();
            selection.insertText(data.content, Word.InsertLocation.end);
            break;

          case "replace":
            if (!data.content)
              throw new Error("Content required for replace operation");
            const selectionReplace = context.document.getSelection();
            selectionReplace.insertText(
              data.content,
              Word.InsertLocation.replace
            );
            break;

          case "replaceParagraph":
            if (!data.content)
              throw new Error(
                "Content required for replaceParagraph operation"
              );
            if (data.paragraphNumber === undefined) {
              throw new Error(
                "Paragraph number required for replaceParagraph operation"
              );
            }
            if (
              data.paragraphNumber < 1 ||
              data.paragraphNumber > totalParagraphs
            ) {
              throw new Error(
                `Paragraph ${data.paragraphNumber} does not exist (total: ${totalParagraphs})`
              );
            }
            const targetParagraphReplace =
              paragraphs.items[data.paragraphNumber - 1];
            const rangeReplace = targetParagraphReplace.getRange();
            rangeReplace.insertText(data.content, Word.InsertLocation.replace);
            break;

          case "delete":
            const selectionDelete = context.document.getSelection();
            selectionDelete.delete();
            break;

          case "deleteParagraph":
            if (data.paragraphNumber === undefined) {
              throw new Error(
                "Paragraph number required for deleteParagraph operation"
              );
            }
            if (
              data.paragraphNumber < 1 ||
              data.paragraphNumber > totalParagraphs
            ) {
              throw new Error(
                `Paragraph ${data.paragraphNumber} does not exist (total: ${totalParagraphs})`
              );
            }
            const targetParagraphDelete =
              paragraphs.items[data.paragraphNumber - 1];
            targetParagraphDelete.delete();
            break;

          default:
            throw new Error(`Unknown operation: ${data.operation}`);
        }

        await context.sync();

        // Recharger le compte de paragraphes après l'opération
        const updatedParagraphs = context.document.body.paragraphs;
        updatedParagraphs.load("items");
        await context.sync();
        const newTotalParagraphs = updatedParagraphs.items.length;

        // Retourner contexte mis à jour
        return {
          operation: data.operation,
          paragraphNumber: data.paragraphNumber,
          previousTotal: totalParagraphs,
          totalParagraphs: newTotalParagraphs,
          message: `${data.operation} completed${
            data.paragraphNumber ? ` on paragraph ${data.paragraphNumber}` : ""
          }. Document now has ${newTotalParagraphs} paragraphs (was ${totalParagraphs}).`,
        };
      });
    },
  });

  client.registerTool("search", {
    description: "Search for text in document, with optional replace functionality",
    schema: {
      type: "object",
      properties: {
        searchText: { type: "string", description: "Text to search for" },
        replaceText: { type: "string", description: "Text to replace with (optional - if not provided, only search is performed)" },
        replaceAll: { type: "boolean", description: "Replace all occurrences (default: true when replaceText is provided)" },
        matchCase: { type: "boolean", description: "Match case sensitive" },
        matchWholeWord: { type: "boolean", description: "Match whole word only (search only)" },
      },
      required: ["searchText"],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        console.log(`[search] Searching for: "${data.searchText}"${data.replaceText ? ` to replace with: "${data.replaceText}"` : ""}`);

        // Rechercher le texte dans le document
        const searchResults = context.document.body.search(data.searchText, {
          matchCase: data.matchCase || false,
          matchWholeWord: data.matchWholeWord || false,
        });

        // Charger les résultats de recherche
        searchResults.load("items");
        await context.sync();

        // Si aucun résultat trouvé
        if (searchResults.items.length === 0) {
          console.log(`[search] No results found for: "${data.searchText}"`);
          return {
            found: false,
            count: 0,
            message: `No occurrences of "${data.searchText}" found in the document.`,
            results: []
          };
        }

        console.log(`[search] Found ${searchResults.items.length} occurrence(s)`);

        // Si replaceText est fourni, effectuer le remplacement
        if (data.replaceText !== undefined) {
          let replaceCount = 0;

          if (data.replaceAll !== false) {
            // Remplacer toutes les occurrences
            searchResults.items.forEach((item) => {
              item.insertText(data.replaceText, Word.InsertLocation.replace);
              replaceCount++;
            });
          } else {
            // Remplacer seulement la première occurrence
            if (searchResults.items.length > 0) {
              searchResults.items[0].insertText(
                data.replaceText,
                Word.InsertLocation.replace
              );
              replaceCount = 1;
            }
          }

          await context.sync();

          // Compter paragraphes pour contexte
          const paras = context.document.body.paragraphs;
          paras.load("items");
          await context.sync();

          return {
            found: true,
            replaced: true,
            count: searchResults.items.length,
            replacedCount: replaceCount,
            searchText: data.searchText,
            replaceText: data.replaceText,
            totalParagraphs: paras.items.length,
            message: `Replaced ${replaceCount} occurrence(s) of "${data.searchText}" with "${data.replaceText}". Document has ${paras.items.length} paragraphs.`,
          };
        }

        // Mode recherche uniquement - retourner les détails des occurrences
        // Charger tous les paragraphes et leur texte en une seule fois
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items/text");

        // Charger le texte de tous les résultats
        for (const result of searchResults.items) {
          result.load("text");
        }

        await context.sync();

        // Créer une liste des paragraphes pour recherche rapide
        const paragraphList = paragraphs.items.map((p, index) => ({
          number: index + 1,
          text: p.text
        }));

        // Traiter chaque résultat de recherche
        const results: Array<{
          occurrence: number;
          paragraphNumber: number;
          text: string;
          context: string;
        }> = [];

        searchResults.items.forEach((result, index) => {
          // Trouver le paragraphe contenant ce résultat
          const matchingPara = paragraphList.find(p => p.text.includes(data.searchText));

          if (matchingPara) {
            // Créer un contexte autour du texte trouvé
            const position = matchingPara.text.indexOf(data.searchText);
            const contextStart = Math.max(0, position - 50);
            const contextEnd = Math.min(matchingPara.text.length, position + data.searchText.length + 50);

            let contextText = matchingPara.text.substring(contextStart, contextEnd);

            // Ajouter des ellipses si nécessaire
            if (contextStart > 0) contextText = "..." + contextText;
            if (contextEnd < matchingPara.text.length) contextText = contextText + "...";

            results.push({
              occurrence: index + 1,
              paragraphNumber: matchingPara.number,
              text: result.text,
              context: contextText
            });
          }
        });

        return {
          found: true,
          replaced: false,
          count: searchResults.items.length,
          searchText: data.searchText,
          message: `Found ${searchResults.items.length} occurrence(s) of "${data.searchText}"`,
          results: results.slice(0, 20) // Limiter à 20 résultats pour ne pas surcharger
        };
      });
    },
  });

  // === FORMATTING TOOLS ===

  client.registerTool("format_paragraph", {
    description: "Apply complete formatting to a paragraph (style, font, alignment, spacing)",
    schema: {
      type: "object",
      properties: {
        paragraphNumber: { type: "integer", description: "Paragraph number to format" },
        style: {
          type: "string",
          description: "Word style to apply",
          enum: [
            "Normal",
            "Heading1",
            "Heading2",
            "Heading3",
            "Heading4",
            "Heading5",
            "Heading6",
            "Title",
            "Subtitle",
            "Quote",
            "IntenseQuote",
            "ListParagraph",
          ],
        },
        bold: { type: "boolean" },
        italic: { type: "boolean" },
        underline: { type: "boolean" },
        font: { type: "string", description: "Font name" },
        fontSize: { type: "number", description: "Font size in points" },
        fontColor: { type: "string", description: "Font color (hex)" },
        alignment: {
          type: "string",
          enum: ["left", "center", "right", "justify"],
        },
        lineSpacing: { type: "number", description: "Line spacing in points" },
        spaceBefore: { type: "number", description: "Space before paragraph in points" },
        spaceAfter: { type: "number", description: "Space after paragraph in points" },
      },
      required: ["paragraphNumber"],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        if (
          data.paragraphNumber < 1 ||
          data.paragraphNumber > paragraphs.items.length
        ) {
          throw new Error(
            `Paragraph ${data.paragraphNumber} does not exist (total: ${paragraphs.items.length})`
          );
        }

        const paragraph = paragraphs.items[data.paragraphNumber - 1];
        const appliedFormats = [];

        // Appliquer le style Word si spécifié
        if (data.style) {
          const styleMap: Record<string, Word.BuiltInStyleName> = {
            Normal: Word.BuiltInStyleName.normal,
            Heading1: Word.BuiltInStyleName.heading1,
            Heading2: Word.BuiltInStyleName.heading2,
            Heading3: Word.BuiltInStyleName.heading3,
            Heading4: Word.BuiltInStyleName.heading4,
            Heading5: Word.BuiltInStyleName.heading5,
            Heading6: Word.BuiltInStyleName.heading6,
            Title: Word.BuiltInStyleName.title,
            Subtitle: Word.BuiltInStyleName.subtitle,
            Quote: Word.BuiltInStyleName.quote,
            IntenseQuote: Word.BuiltInStyleName.intenseQuote,
            ListParagraph: Word.BuiltInStyleName.listParagraph,
          };

          const builtInStyle = styleMap[data.style];
          if (builtInStyle) {
            paragraph.styleBuiltIn = builtInStyle;
            appliedFormats.push(`style: ${data.style}`);
          }
        }

        // Appliquer le formatage de police
        const font = paragraph.font;
        if (data.bold !== undefined) {
          font.bold = data.bold;
          appliedFormats.push(`bold: ${data.bold}`);
        }
        if (data.italic !== undefined) {
          font.italic = data.italic;
          appliedFormats.push(`italic: ${data.italic}`);
        }
        if (data.underline !== undefined) {
          font.underline = data.underline
            ? Word.UnderlineType.single
            : Word.UnderlineType.none;
          appliedFormats.push(`underline: ${data.underline}`);
        }
        if (data.font) {
          font.name = data.font;
          appliedFormats.push(`font: ${data.font}`);
        }
        if (data.fontSize) {
          font.size = data.fontSize;
          appliedFormats.push(`size: ${data.fontSize}pt`);
        }
        if (data.fontColor) {
          font.color = data.fontColor;
          appliedFormats.push(`color: ${data.fontColor}`);
        }

        // Appliquer l'alignement
        if (data.alignment) {
          const alignmentMap: Record<string, Word.Alignment> = {
            left: Word.Alignment.left,
            center: Word.Alignment.centered,
            right: Word.Alignment.right,
            justify: Word.Alignment.justified,
          };

          paragraph.alignment = alignmentMap[data.alignment];
          appliedFormats.push(`align: ${data.alignment}`);
        }

        // Appliquer l'espacement
        if (data.lineSpacing !== undefined) {
          paragraph.lineSpacing = data.lineSpacing;
          appliedFormats.push(`line spacing: ${data.lineSpacing}pt`);
        }
        if (data.spaceBefore !== undefined) {
          paragraph.spaceBefore = data.spaceBefore;
          appliedFormats.push(`space before: ${data.spaceBefore}pt`);
        }
        if (data.spaceAfter !== undefined) {
          paragraph.spaceAfter = data.spaceAfter;
          appliedFormats.push(`space after: ${data.spaceAfter}pt`);
        }

        await context.sync();

        const message = appliedFormats.length > 0
          ? `Formatting applied to paragraph ${data.paragraphNumber}: ${appliedFormats.join(", ")}`
          : `No formatting changes for paragraph ${data.paragraphNumber}`;

        return {
          paragraphNumber: data.paragraphNumber,
          appliedFormats: appliedFormats,
          totalParagraphs: paragraphs.items.length,
          message: message,
        };
      });
    },
  });

  // === TABLE TOOLS ===

  client.registerTool("insert_table", {
    description: "Insert a table",
    schema: {
      type: "object",
      properties: {
        afterParagraphNumber: { type: "integer" },
        rows: { type: "integer" },
        columns: { type: "integer" },
        headerRow: { type: "boolean" },
        data: {
          type: "array",
          items: {
            type: "array",
            items: { type: "string" },
          },
        },
      },
      required: ["afterParagraphNumber", "rows", "columns"],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        if (
          data.afterParagraphNumber < 1 ||
          data.afterParagraphNumber > paragraphs.items.length
        ) {
          throw new Error(
            `Paragraph ${data.afterParagraphNumber} does not exist (total: ${paragraphs.items.length})`
          );
        }

        const paragraph = paragraphs.items[data.afterParagraphNumber - 1];
        const range = paragraph.getRange(Word.RangeLocation.after);

        const table = range.insertTable(
          data.rows,
          data.columns,
          Word.InsertLocation.after,
          data.data || []
        );

        if (data.headerRow !== false && data.rows > 0) {
          table.rows.getFirst().load();
          await context.sync();
          table.rows.getFirst().shadingColor = "#4472C4";
          table.rows.getFirst().font.color = "#FFFFFF";
          table.rows.getFirst().font.bold = true;
        }

        await context.sync();
        // Compter les tableaux
        const allTables = context.document.body.tables;
        allTables.load("items");
        await context.sync();

        return {
          tableIndex: allTables.items.length - 1,
          rows: data.rows,
          columns: data.columns,
          afterParagraphNumber: data.afterParagraphNumber,
          totalTables: allTables.items.length,
          message: `Table ${allTables.items.length - 1} created (${data.rows}x${
            data.columns
          }) after paragraph ${data.afterParagraphNumber}. Total tables: ${
            allTables.items.length
          }`,
        };
      });
    },
  });

  client.registerTool("get_table_info", {
    description: "Get table information",
    schema: {
      type: "object",
      properties: {
        tableIndex: { type: "integer" },
      },
      required: [],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items");
        await context.sync();

        console.log("[get_table_info] Found", tables.items.length, "table(s)");

        if (tables.items.length === 0) {
          console.log(
            "[get_table_info] No tables in document, returning empty array"
          );
          return []; // Retourner tableau vide au lieu d'erreur
        }

        const result: Array<{
          index: number;
          rows: number;
          columns: number;
          data: string[][];
        }> = [];

        const tablesToProcess =
          data.tableIndex !== undefined
            ? [tables.items[data.tableIndex]]
            : tables.items;

        if (
          data.tableIndex !== undefined &&
          (data.tableIndex < 0 || data.tableIndex >= tables.items.length)
        ) {
          throw new Error(
            `Table index ${data.tableIndex} does not exist (total: ${tables.items.length})`
          );
        }

        for (let i = 0; i < tablesToProcess.length; i++) {
          const table = tablesToProcess[i];
          // Charger toutes les propriétés nécessaires en une seule fois
          table.load("rowCount, columnCount, rows/items/cells/items/body/text");
          await context.sync();

          const tableData: string[][] = [];

          // Parcourir les lignes et cellules déjà chargées
          for (const row of table.rows.items) {
            const rowData: string[] = [];
            for (const cell of row.cells.items) {
              rowData.push(cell.body.text.trim());
            }
            tableData.push(rowData);
          }

          result.push({
            index: data.tableIndex !== undefined ? data.tableIndex : i,
            rows: table.rowCount,
            columns: tableData.length > 0 ? tableData[0].length : 0,
            data: tableData,
          });
        }

        return result;
      });
    },
  });

  client.registerTool("edit_table_cell", {
    description: "Edit table cell content",
    schema: {
      type: "object",
      properties: {
        tableIndex: { type: "integer" },
        rowIndex: { type: "integer" },
        columnIndex: { type: "integer" },
        content: { type: "string" },
      },
      required: ["tableIndex", "rowIndex", "columnIndex", "content"],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        const tables = context.document.body.tables;
        tables.load("items");
        await context.sync();

        if (data.tableIndex < 0 || data.tableIndex >= tables.items.length) {
          throw new Error(
            `Table ${data.tableIndex} does not exist (total: ${tables.items.length})`
          );
        }

        const table = tables.items[data.tableIndex];
        table.load("rowCount");
        await context.sync();

        if (data.rowIndex < 0 || data.rowIndex >= table.rowCount) {
          throw new Error(
            `Row ${data.rowIndex} does not exist (total: ${table.rowCount})`
          );
        }

        table.rows.load("items");
        await context.sync();
        const firstRow = table.rows.items[0];
        firstRow.load("cells");
        await context.sync();
        firstRow.cells.load("items");
        await context.sync();
        const columnCount = firstRow.cells.items.length;

        if (data.columnIndex < 0 || data.columnIndex >= columnCount) {
          throw new Error(
            `Column ${data.columnIndex} does not exist (total: ${columnCount})`
          );
        }

        const cell = table.getCell(data.rowIndex, data.columnIndex);
        cell.body.clear();
        cell.body.insertText(data.content, Word.InsertLocation.start);

        await context.sync();
        return `Cell [${data.rowIndex}, ${data.columnIndex}] updated in table ${data.tableIndex}`;
      });
    },
  });

  // === COMMENT TOOLS ===

  client.registerTool("add_comment", {
    description: "Add a comment to a paragraph or specific text in the document",
    schema: {
      type: "object",
      properties: {
        paragraphNumber: { type: "integer" },
        commentText: { type: "string" },
        author: { type: "string" },
      },
      required: ["paragraphNumber", "commentText"],
    },
    handler: async (data) => {
      return Word.run(async (context) => {
        const paragraphs = context.document.body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        if (
          data.paragraphNumber < 1 ||
          data.paragraphNumber > paragraphs.items.length
        ) {
          throw new Error(
            `Paragraph ${data.paragraphNumber} does not exist (total: ${paragraphs.items.length})`
          );
        }

        const paragraph = paragraphs.items[data.paragraphNumber - 1];
        paragraph.load("text");
        await context.sync();

        const range = paragraph.getRange();
        const author = data.author || "AI Assistant";

        try {
          const fullCommentText =
            author !== "AI Assistant"
              ? `[${author}] ${data.commentText}`
              : data.commentText;

          const comment = (range as any).insertComment(fullCommentText);
          comment.load("id, content");
          await context.sync();

          return `Comment added to paragraph ${data.paragraphNumber}: "${data.commentText}"`;
        } catch (e) {
          const endRange = paragraph.getRange(Word.RangeLocation.end);
          const commentMark = endRange.insertText(
            ` 💬`,
            Word.InsertLocation.after
          );
          commentMark.font.color = "#7C5DFA";
          commentMark.font.size = 10;

          const commentTextRange = commentMark.insertText(
            ` [${author}: ${data.commentText}]`,
            Word.InsertLocation.after
          );
          commentTextRange.font.color = "#888888";
          commentTextRange.font.size = 9;
          commentTextRange.font.italic = true;

          await context.sync();
          return `Comment added to paragraph ${data.paragraphNumber} by ${author} (inline annotation)`;
        }
      });
    },
  });

  client.registerTool("get_comments", {
    description: "Get all comments",
    schema: {
      type: "object",
      properties: {},
      required: [],
    },
    handler: async () => {
      console.log("[get_comments] Starting handler");

      return Word.run(async (context) => {
        const comments: Array<{
          id: string;
          text: string;
          author: string;
          paragraphNumber: number;
          date?: string;
          resolved?: boolean;
        }> = [];

        try {
          console.log("[get_comments] Trying to access comments...");

          // Essayer d'accéder à la collection de commentaires
          // Note: Cette API nécessite WordApi 1.4+ (Office 2019, Word Online)
          const body = context.document.body;

          // Essayer d'utiliser body.getComments() si disponible
          try {
            console.log("[get_comments] Attempting body.getComments()...");
            const bodyComments = (body as any).getComments();

            if (bodyComments) {
              bodyComments.load("items");
              await context.sync();

              console.log(
                "[get_comments] ✅ Found",
                bodyComments.items.length,
                "comments via body.getComments()"
              );

              for (let i = 0; i < bodyComments.items.length; i++) {
                const comment = bodyComments.items[i];
                comment.load("id, content, authorName, creationDate, resolved");
                await context.sync();

                let content = comment.content || "";
                let author = comment.authorName || "Unknown";

                // Parser [author] prefix
                const match = content.match(/^\[([^\]]+)\]\s*(.+)$/);
                if (match) {
                  author = match[1];
                  content = match[2];
                }

                comments.push({
                  id: comment.id,
                  text: content,
                  author,
                  paragraphNumber: 0,
                  date: comment.creationDate,
                  resolved: comment.resolved,
                });
              }

              console.log(
                "[get_comments] ✅ Successfully retrieved",
                comments.length,
                "comments"
              );
              return comments;
            }
          } catch (bodyError) {
            console.log(
              "[get_comments] body.getComments() not available:",
              bodyError
            );
          }

          // Si aucune méthode ne fonctionne
          console.log(
            "[get_comments] ⚠️ Comments API not available in this Word version"
          );
          console.log(
            "[get_comments] Note: Comments require WordApi 1.4+ (Office 2019 or Word Online)"
          );
        } catch (e) {
          console.error("[get_comments] Unexpected error:", e);
        }

        console.log("[get_comments] Returning", comments.length, "comments");
        return comments;
      });
    },
  });

  client.registerTool("delete_comment", {
    description: "Delete a comment by its ID (get ID from get_comments)",
    schema: {
      type: "object",
      properties: {
        commentId: {
          type: "string",
          description: "Comment ID to delete (from get_comments)",
        },
      },
      required: ["commentId"],
    },
    handler: async (data) => {
      console.log("[delete_comment] Deleting comment:", data.commentId);

      return Word.run(async (context) => {
        try {
          const body = context.document.body;
          const bodyComments = (body as any).getComments();

          if (!bodyComments) {
            throw new Error(
              "Comments API not available (requires WordApi 1.4+)"
            );
          }

          console.log("[delete_comment] Loading comments...");
          bodyComments.load("items");
          await context.sync();

          console.log(
            "[delete_comment] Total comments:",
            bodyComments.items.length
          );

          let found = false;
          for (const comment of bodyComments.items) {
            comment.load("id");
            await context.sync();

            console.log("[delete_comment] Checking comment ID:", comment.id);

            if (
              comment.id === data.commentId ||
              comment.id === String(data.commentId)
            ) {
              console.log("[delete_comment] ✅ Comment found! Deleting...");
              comment.delete();
              await context.sync();
              console.log("[delete_comment] ✅ Delete sync complete");
              found = true;
              break;
            }
          }

          if (!found) {
            console.error(
              "[delete_comment] ❌ Comment not found. Available IDs:",
              bodyComments.items.map((c: any) => c.id).join(", ")
            );
            throw new Error(`Comment ${data.commentId} not found`);
          }

          return `Comment ${data.commentId} deleted successfully`;
        } catch (e) {
          console.error("[delete_comment] Error:", e);
          throw new Error(
            `Could not delete comment: ${
              e instanceof Error ? e.message : "Unknown error"
            }`
          );
        }
      });
    },
  });

  // === IMAGE TOOL ===

  client.registerTool("insert_image", {
    description:
      "Insert an image from URL. Use HTTPS URLs (http://localhost not supported due to CORS).",
    schema: {
      type: "object",
      properties: {
        imageUrl: {
          type: "string",
          description: "Image URL (HTTPS recommended, not localhost)",
        },
        width: {
          type: "integer",
          description: "Width in pixels (default: 400)",
        },
        height: { type: "integer", description: "Height in pixels (optional)" },
        afterParagraphNumber: {
          type: "integer",
          description: "Insert after this paragraph",
        },
      },
      required: ["imageUrl"],
    },
    handler: async (data) => {
      console.log("[insert_image] Processing image:", data.imageUrl);

      let base64: string;

      try {
        // Essayer de télécharger l'image côté client
        console.log("[insert_image] Fetching image from URL...");

        const response = await fetch(data.imageUrl, {
          mode: "cors",
          cache: "no-cache",
        });

        if (!response.ok) {
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }

        const blob = await response.blob();
        console.log(
          "[insert_image] Image downloaded, size:",
          blob.size,
          "type:",
          blob.type
        );

        if (blob.size === 0) {
          throw new Error("Image is empty (0 bytes)");
        }

        // Convertir en base64
        base64 = await new Promise<string>((resolve, reject) => {
          const reader = new FileReader();
          reader.onloadend = () => {
            const result = reader.result as string;
            if (!result || typeof result !== "string") {
              reject(new Error("Failed to read image as base64"));
              return;
            }
            const parts = result.split(",");
            if (parts.length !== 2) {
              reject(new Error("Invalid base64 data URL"));
              return;
            }
            resolve(parts[1]);
          };
          reader.onerror = () => reject(new Error("FileReader error"));
          reader.readAsDataURL(blob);
        });

        console.log(
          "[insert_image] Converted to base64, length:",
          base64.length
        );
      } catch (fetchError) {
        console.error("[insert_image] ❌ Failed to fetch image:", fetchError);
        throw new Error(
          `Cannot download image: ${
            fetchError instanceof Error ? fetchError.message : "Unknown error"
          }. Note: localhost URLs may fail due to CORS.`
        );
      }

      return Word.run(async (context) => {
        const body = context.document.body;
        const paragraphs = body.paragraphs;
        paragraphs.load("items");
        await context.sync();

        let insertionPoint: Word.Range;

        if (
          data.afterParagraphNumber !== undefined &&
          data.afterParagraphNumber > 0
        ) {
          if (data.afterParagraphNumber > paragraphs.items.length) {
            throw new Error(
              `Paragraph ${data.afterParagraphNumber} does not exist (total: ${paragraphs.items.length})`
            );
          }
          const targetPara = paragraphs.items[data.afterParagraphNumber - 1];
          insertionPoint = targetPara.getRange(Word.RangeLocation.after);
        } else {
          insertionPoint = body.getRange(Word.RangeLocation.end);
        }

        console.log("[insert_image] Inserting image into Word...");

        const inlinePicture = insertionPoint.insertInlinePictureFromBase64(
          base64,
          Word.InsertLocation.end
        );

        if (data.width) {
          inlinePicture.width = data.width;
        }
        if (data.height) {
          inlinePicture.height = data.height;
        } else if (data.width) {
          inlinePicture.lockAspectRatio = true;
        }

        insertionPoint.insertText("\n", Word.InsertLocation.end);

        await context.sync();

        console.log("[insert_image] ✅ Image inserted successfully");

        return `Image inserted from ${data.imageUrl} (${data.width || 400}px${
          data.height ? ` x ${data.height}px` : ""
        })`;
      });
    },
  });

  console.log(
    `[WordClient] ✅ Registered ${client.getToolsConfig().length} tools`
  );
}
